import React, { useEffect, useRef, useState } from 'react';
import TemplateComponent from '../components/TemplateComponent';
import { Template } from '../interface/template';
import NotFoundTemplate from '../components/NotFoundTemplate';
import { DeviceDetails } from '../interface/device.details';
import { applyBodyStyles } from '../utils/common.utils';
import "../components/trip.css";

interface ConnectDevicePageProp {
  deviceData: DeviceDetails,
}

const ConnectDevicePage: React.FC<ConnectDevicePageProp> = ({ deviceData }) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rowHeight, setRowHeight] = useState(10);
  const [footerHeight, setFooterHeight] = useState(10);
  const [headerHeight, setHeaderHeight] = useState(10);
  const [tableHeaderHeight, settableHeaderHeight] = useState(10);
  const [selectedTemplate, setSelectedTemplate] = useState<Template>();
  const [fetchError, setFetchError] = useState(false);
  const [showData, setShowData] = useState(false);
  const [isMatrix, setIsMatrix] = useState<boolean>();
  const [index, setIndex] = useState<number>();
  const [startIncrement, setStartIncrement] = useState<number>();
  const [endIncrement, setEndIncrement] = useState<number>();
  const [totalDevices, setTotalDevices] = useState<number>();
  const [onPageLoad, setOnPageLoad] = useState<boolean>(true);
  const [isLastChild, setIsLastChild] = useState<boolean>();
  const [isChild, setIsChild] = useState<boolean>();

  useEffect(() => {
    handleSearchDevice();
    getTemplate()
  }, [deviceData]);

  const handleSearchDevice = async () => {
    try {
      const isMatrix = Array.isArray(deviceData.displayLayoutsOrder[0]);
      setIsMatrix(isMatrix);
      let isChild = false;
      let isLastChild = false;
      if (isMatrix) {
        const matrixDevices = calculateIndexes(deviceData.displayLayoutsOrder as []);
        const foundDevice = matrixDevices.find(x => x.device === deviceData._id);
        isChild = foundDevice?.isChild || false;
        isLastChild = foundDevice?.isLast || false;
        setIndex(foundDevice?.flatIndex);  // ✅ FIXED: Use flatIndex instead of startIndex
        setStartIncrement(foundDevice?.startIncrement);
        setEndIncrement(foundDevice?.endIncrement);
        setTotalDevices(matrixDevices.length);
      }
      else {
        setTotalDevices(deviceData?.displayLayoutsOrder.length);
        const indx = deviceData.displayLayoutsOrder.findIndex(x => x === deviceData._id);
        if (indx !== -1) setIndex(indx);
      }
      setIsChild(isChild);
      setIsLastChild(isLastChild);

    } catch (error: any) {
      console.error('Error fetching device or template:', error);
    }
  };

  useEffect(() => {
    if (deviceData && selectedTemplate && onPageLoad) {
      setShowData(true);
      const styles = selectedTemplate?.body?.inLineStyle;
      applyBodyStyles(styles);
      setOnPageLoad(false);
    }
    else if (selectedTemplate) {
      const styles = selectedTemplate?.body?.inLineStyle;
      applyBodyStyles(styles);
    }
  }, [onPageLoad, selectedTemplate]);

  const getTemplate = async () => {
    try {
      if (deviceData?.templateId) {
        const templateResult = deviceData?.templateId as Template;
        if (templateResult) {
          setSelectedTemplate(templateResult);
        }
      }
    } catch (error) {
      console.error('getTemplate failed:', error);
    }
  }

  const calculateRowsPerPage = () => {

    const screenHeight = window.innerHeight;
    const headerHeight = 120;
    const approxRowHeight = 70;
    const footerHeight = ((!isMatrix && deviceData?.enableFooter)) ? 50 : 0;
    const tableHeaderHeight = ((!isMatrix && !deviceData?.isChild)) ? 54 : 0;

    const availableHeight = screenHeight - headerHeight - footerHeight - tableHeaderHeight;

    const numRows = Math.floor(availableHeight / approxRowHeight);
    const dynamicRowHeight = availableHeight / numRows;

    setFooterHeight(footerHeight)
    if (deviceData.rowsPerPage) setRowsPerPage(deviceData.rowsPerPage);
    else setRowsPerPage(numRows);
    setHeaderHeight(headerHeight);
    settableHeaderHeight(tableHeaderHeight);
    setRowHeight(dynamicRowHeight);
  };

  useEffect(() => {
    calculateRowsPerPage();
    window.addEventListener('resize', calculateRowsPerPage);
    return () => window.removeEventListener('resize', calculateRowsPerPage);
  }, [deviceData]);

  /**
   * ✅ FIXED VERSION - Properly calculates flat index and cumulative increments
   * 
   * Key concepts:
   * - flatIndex: The device's position across ALL devices (0, 1, 2, 3, 4, 5 for 6 devices)
   * - startIncrement: cumulative extra rows from ALL previous devices
   * - endIncrement: startIncrement + this device's extra rows
   * 
   * Example for 2 rows × 3 devices:
   * D0 (row 0, pos 0): flatIndex=0, startIncrement=0, extraRows=0, endIncrement=0
   * D1 (row 0, pos 1): flatIndex=1, startIncrement=0, extraRows=2, endIncrement=2
   * D2 (row 0, pos 2): flatIndex=2, startIncrement=2, extraRows=2, endIncrement=4
   * D3 (row 1, pos 0): flatIndex=3, startIncrement=4, extraRows=0, endIncrement=4
   * D4 (row 1, pos 1): flatIndex=4, startIncrement=4, extraRows=2, endIncrement=6
   * D5 (row 1, pos 2): flatIndex=5, startIncrement=6, extraRows=2, endIncrement=8
   */
  const calculateIndexes = (displayLayoutsOrder: string[][]) => {
    let result = [];
    let cumulativeExtraRows = 0; // Track cumulative extra rows across all devices
    let flatIndex = 0; // Track the flat device index across all rows

    for (let rowIndex = 0; rowIndex < displayLayoutsOrder.length; rowIndex++) {
      let row = displayLayoutsOrder[rowIndex];

      for (let deviceIndex = 0; deviceIndex < row.length; deviceIndex++) {
        let device = row[deviceIndex];

        let isFirst = deviceIndex === 0;
        let isLast = deviceIndex === row.length - 1;
        let isMiddle = !isFirst && !isLast;
        let isChild = deviceIndex > 0;

        // Determine extra rows for this device
        // Middle devices and last devices get +2 extra rows
        let extraRows = 0;
        if (isMiddle || isLast) {
          extraRows = 2;
        }

        // startIncrement = cumulative extra rows from ALL previous devices
        let startIncrement = cumulativeExtraRows;

        // endIncrement = cumulative + this device's extra rows
        let endIncrement = cumulativeExtraRows + extraRows;

        // Update cumulative counter for next device
        cumulativeExtraRows = endIncrement;

        result.push({
          device,
          flatIndex,  // ✅ ADDED: Store the flat index for use in TemplateComponent
          isLast,
          isChild,
          startIncrement,
          endIncrement,
          extraRows
        });

        flatIndex++; // Increment for next device
      }
    }

    return result;
  };

  return (
    <div style={{ height: "100vh", overflow: "hidden" }}>
      <div>
        {selectedTemplate && showData && (
          <div>
            {selectedTemplate?._id && (
              <TemplateComponent
                fontSize={selectedTemplate.pageFontSize || "40px"}
                deviceId={deviceData?._id}
                cluster={deviceData?.cluster}
                template={selectedTemplate}
                rowsPerPage={rowsPerPage}
                footerHeight={footerHeight}
                flipOffsetAtSeconds={deviceData?.flipOffsetAtSeconds || 15}
                tableHeaderHeight={tableHeaderHeight}
                sequence={deviceData?.sequence || 1}
                flip={deviceData?.flip || 0}
                isChild={isChild}
                isLastChild={isLastChild}
                totalDevices={totalDevices || 1}
                displayLayoutsOrder={deviceData?.displayLayoutsOrder || []}
                startIncrement={startIncrement}
                endIncrement={endIncrement}
                enableFooter={deviceData?.enableFooter}
                deviceIndex={index}
                isMatrix={isMatrix}
                advertisement={deviceData?.advertisement}
                rowHeight={rowHeight}
                headerHeight={headerHeight}
                announcement={deviceData?.announcement}
                onBackward={() => setShowData(false)}
              ></TemplateComponent>
            )}
          </div>
        )}

        {fetchError && <NotFoundTemplate />}
      </div>
    </div>
  );
};

export default ConnectDevicePage;